import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import { useCallback, useMemo, useState } from 'react';
import { useMediaBlobUrl } from '../../hooks/useMediaBlobUrl';
import {
  buildGoogleGviewEmbedUrl,
  extractFilePreviewUrl,
  isImagePreviewContent,
  isImagePreviewUrl,
  shouldUseLocalBlobPreview,
} from '../../utils/filePreview';
import { normalizeMediaUrl } from '../../utils/normalizeMediaUrl';

type FileDocumentPreviewProps = {
  previewUrl?: string | null;
  conteudo?: string | null;
  title?: string;
  height?: string;
  showDownload?: boolean;
  downloadFileName?: string;
  footerAction?: React.ReactNode;
};

export default function FileDocumentPreview({
  previewUrl,
  conteudo,
  title = 'Documento',
  height = '60vh',
  showDownload = true,
  downloadFileName,
  footerAction,
}: FileDocumentPreviewProps): JSX.Element {
  const [iframeLoading, setIframeLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fileSourceUrl = useMemo(() => {
    return normalizeMediaUrl(previewUrl) || extractFilePreviewUrl(conteudo);
  }, [previewUrl, conteudo]);

  const isImage =
    isImagePreviewUrl(fileSourceUrl) || isImagePreviewContent(conteudo);
  const useBlobPreview =
    !isImage && shouldUseLocalBlobPreview(fileSourceUrl);
  const { blobUrl, loading: blobLoading, error: blobError } = useMediaBlobUrl(
    useBlobPreview ? fileSourceUrl : null,
  );
  // Fallback secundário: só recorre ao Google Docs Viewer se o blob
  // preview autenticado falhar (ex.: tipo de arquivo que o blob não sabe
  // renderizar). Não usar como estratégia padrão: o Google busca a URL
  // publicamente, sem Authorization, e não é compatível com documentos
  // protegidos por LGPD.
  const useGviewFallback = useBlobPreview && blobError;

  const handleDownload = useCallback(async () => {
    if (!fileSourceUrl || downloading) {
      return;
    }

    setDownloading(true);
    try {
      const response = await fetch(fileSourceUrl);
      if (!response.ok) {
        throw new Error('Falha ao baixar o arquivo');
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = downloadFileName || title || 'documento.pdf';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(fileSourceUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
    }
  }, [downloadFileName, downloading, fileSourceUrl, title]);

  if (!fileSourceUrl) {
    return (
      <Box py={2}>
        <Typography>
          Não foi possível localizar o arquivo para visualização.
        </Typography>
      </Box>
    );
  }

  if (useBlobPreview && blobLoading) {
    return (
      <Box textAlign='center' py={5}>
        <CircularProgress />
        <Typography style={{ marginTop: 16 }}>
          Carregando documento...
        </Typography>
      </Box>
    );
  }

  const renderFooter = () => {
    if (footerAction) {
      return <Box mt={2}>{footerAction}</Box>;
    }
    if (!showDownload) {
      return null;
    }
    return (
      <Button
        color='primary'
        variant='contained'
        onClick={handleDownload}
        disabled={downloading}
        style={{ marginTop: 16 }}
      >
        {downloading ? 'Baixando...' : 'Download'}
      </Button>
    );
  };

  if (isImage) {
    return (
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        minHeight={height}
      >
        <img
          src={fileSourceUrl}
          alt={title}
          style={{
            maxWidth: '100%',
            maxHeight: height,
            objectFit: 'contain',
            border: '1px solid #ccc',
          }}
        />
        {renderFooter()}
      </Box>
    );
  }

  const iframeSrc = useBlobPreview && !blobError
    ? blobUrl
    : buildGoogleGviewEmbedUrl(fileSourceUrl);

  // Só chegamos aqui sem iframeSrc se o blob preview falhou e o arquivo
  // ainda assim não pôde ser embutido via Google Docs Viewer (ex.: sem
  // fileSourceUrl). Nesse caso, exibir mensagem de erro real em vez de
  // deixar a área em branco.
  if (!iframeSrc) {
    return (
      <Box py={2} textAlign='center'>
        <Typography gutterBottom>
          Não foi possível exibir o documento nesta tela.
        </Typography>
        <Typography>
          <a href={fileSourceUrl} target='_blank' rel='noopener noreferrer'>
            Abrir arquivo em nova aba
          </a>
        </Typography>
        {renderFooter()}
      </Box>
    );
  }

  return (
    <Box display='flex' flexDirection='column' alignItems='center' minHeight={height}>
      {iframeLoading && <CircularProgress style={{ margin: '16px 0' }} />}
      <iframe
        src={iframeSrc}
        title={title}
        style={{
          width: '100%',
          height,
          border: 'none',
          display: iframeLoading ? 'none' : 'block',
        }}
        onLoad={() => setIframeLoading(false)}
        onError={() => setIframeLoading(false)}
      />
      {!iframeLoading && !footerAction && showDownload && (
        <Typography
          align='center'
          style={{ marginTop: 8, fontSize: 14, color: '#666' }}
        >
          {useGviewFallback
            ? 'Não foi possível carregar a pré-visualização autenticada. Se o documento acima aparecer em branco, use o botão de download abaixo.'
            : 'Se o documento não carregou, use o botão de download abaixo.'}
        </Typography>
      )}
      {renderFooter()}
    </Box>
  );
}
